import React from 'react';

interface TerminalThankYouProps {
  /** Testids for the card root and the body message paragraph. */
  testids: { root: string; message: string };
  /** Big terminal headline, e.g. "Thank You!". */
  headline: string;
  /** Body copy; `whitespace-pre-line` preserves the copy's own paragraph breaks. */
  body: React.ReactNode;
  /**
   * Extra attributes for the body paragraph — the PHQ terminal pins a
   * `data-reason` here (its e2e contract asserts on it). Kept generic so the card
   * itself owns no flow-specific ids.
   */
  messageProps?: React.HTMLAttributes<HTMLParagraphElement> & {
    [key: `data-${string}`]: string | undefined;
  };
  /** Optional muted footnote under the body (e.g. the already-locked note). */
  footnote?: React.ReactNode;
}

/**
 * Shared terminal "thank-you" card — the full-width `/thanks`-style confirmation
 * (headline + body + waving-man image) used by BOTH the selection flow's
 * `SelectionDone` (screen 11) and the PHQ flow's `/e/:token/done` terminal
 * (screens 3/10 + returning lockout). Presentational only: no store reads; copy,
 * testids and any extra message attributes come entirely from props so each flow
 * keeps its own pinned strings + ids.
 */
export const TerminalThankYou: React.FC<TerminalThankYouProps> = ({
  testids,
  headline,
  body,
  messageProps,
  footnote,
}) => {
  return (
    <div
      data-testid={testids.root}
      className="relative flex h-full w-full flex-1 flex-col justify-between gap-4 overflow-hidden rounded-3xl bg-card 2xl:flex-row"
    >
      <article className="z-10 flex flex-col gap-4 px-6 pt-12 pb-10 max-md:items-center max-md:text-center md:pt-20 md:pl-12 min-2xl:min-w-82 min-3xl:max-w-[650px]">
        <h1 className="text-3xl font-extrabold text-[#1a1a2e] lg:text-5xl max-sm:text-2xl">
          {headline}
        </h1>
        <p
          data-testid={testids.message}
          className="whitespace-pre-line text-[17px] leading-7 text-[#5a5a70] lg:text-xl"
          {...messageProps}
        >
          {body}
        </p>
        {footnote && (
          <p className="text-[14px] leading-6 text-[#8a8aa0]">{footnote}</p>
        )}
      </article>
      <img
        src="/images/thanks.png"
        alt=""
        aria-hidden="true"
        className="mt-auto flex h-full max-h-[560px] object-cover object-left-top"
      />
    </div>
  );
};
